/** Agrégation des scores pour graphiques joueur / équipe (interface moderne). */

export const PLAYER_SCORE_BUCKETS = [
	{ id: 'b0', label: '< 40', color: '#FF7070', colorEnd: '#E85555', match: (s) => s < 40 },
	{ id: 'b1', label: '40 – 49', color: '#FFB347', colorEnd: '#F59E42', match: (s) => s >= 40 && s < 49 },
	{ id: 'b2', label: '49 – 55', color: '#6EB5FF', colorEnd: '#4A9EFF', match: (s) => s >= 49 && s < 55 },
	{ id: 'b3', label: '≥ 55', color: '#5CD69A', colorEnd: '#3BC47E', match: (s) => s >= 55 },
];

export const TEAM_SCORE_BUCKETS = [
	{ id: 't0', label: '< 160', color: '#FF7070', colorEnd: '#E85555', match: (s) => s < 160 },
	{ id: 't1', label: '160 – 185', color: '#FFB347', colorEnd: '#F59E42', match: (s) => s >= 160 && s <= 185 },
	{ id: 't2', label: '185 – 210', color: '#6EB5FF', colorEnd: '#4A9EFF', match: (s) => s > 185 && s <= 210 },
	{ id: 't3', label: '> 210', color: '#5CD69A', colorEnd: '#3BC47E', match: (s) => s > 210 },
];

/** @deprecated Utiliser PLAYER_SCORE_BUCKETS */
export const SCORE_BUCKETS = PLAYER_SCORE_BUCKETS;

/** Parts P1–P4 dans la série (part du total quilles saison). */
export const PLAYER_SERIES_PARTY_BUCKETS = [
	{ id: 'p1', label: 'P1', color: '#6EB5FF', colorEnd: '#4A9EFF', key: 'quilles1' },
	{ id: 'p2', label: 'P2', color: '#5CD69A', colorEnd: '#3BC47E', key: 'quilles2' },
	{ id: 'p3', label: 'P3', color: '#FFB347', colorEnd: '#F59E42', key: 'quilles3' },
	{ id: 'p4', label: 'P4', color: '#B794F6', colorEnd: '#9F7AEA', key: 'quilles4' },
];

export const CHART_VARIANTS = {
	player: {
		scoreBuckets: PLAYER_SCORE_BUCKETS,
		seriesPartyBuckets: PLAYER_SERIES_PARTY_BUCKETS,
		yFloor: 55,
		yStep: 20,
		barsTitle: 'Parties',
		donutTitle: 'Répartition des scores',
		seriesPartyBarsTitle: 'Répartition par partie — vs équilibre',
	},
	team: {
		scoreBuckets: TEAM_SCORE_BUCKETS,
		yFloor: 220,
		yStep: 20,
		yBase: 160,
		barsTitle: 'Parties équipe',
		donutTitle: 'Répartition des parties',
	},
};

/** Teintes par manche (vue saison sans année) ou par saison (carrière). */
const MANCHE_HUES = [210, 168, 145, 195, 175];
const SEASON_HUES = [210, 168, 145, 195, 175, 220, 185, 155, 200, 160, 140, 180, 205, 170, 150];

function collectPartyScores(rounds) {
	const scores = [];
	rounds.forEach((round) => {
		[round.quilles1, round.quilles2, round.quilles3, round.quilles4].forEach((q) => {
			const n = Number(q);
			if (n > 0) scores.push(n);
		});
	});
	return scores;
}

/** Manches en ordre chronologique (saison croissante, puis manche croissante). */
export function compareRoundsChronological(a, b) {
	if (a.seasonYear != null && b.seasonYear != null && a.seasonYear !== b.seasonYear) {
		return a.seasonYear - b.seasonYear;
	}
	const byRound = a.round.localeCompare(b.round, undefined, { numeric: true });
	if (byRound !== 0) return byRound;
	const byChamp = (a.championship ?? '').localeCompare(b.championship ?? '', 'fr');
	if (byChamp !== 0) return byChamp;
	return (a.category ?? '').localeCompare(b.category ?? '', 'fr');
}

/** Points séquentiels : P1.1 … P1.4, P2.1 … (jusqu'à 4 parties par manche). */
export function computeSequentialParties(rounds) {
	const sorted = [...rounds].sort(compareRoundsChronological);

	const points = [];
	sorted.forEach((round) => {
		const manche = Number(round.round);
		[
			{ part: 1, value: round.quilles1 },
			{ part: 2, value: round.quilles2 },
			{ part: 3, value: round.quilles3 },
			{ part: 4, value: round.quilles4 },
		].forEach(({ part, value }) => {
			const n = Number(value);
			if (n > 0) {
				points.push({
					label: `P${manche}.${part}`,
					seasonYear: round.seasonYear ?? null,
					manche,
					part,
					value: n,
				});
			}
		});
	});
	return points;
}

export function computePartiesAverage(points) {
	if (!points?.length) return 0;
	return points.reduce((s, p) => s + p.value, 0) / points.length;
}

/** Synthèse équipe : une « partie » = P{manche}.{1|2|3|4} (comme le graphique). */
export function computeTeamPartySummary(rounds) {
	const points = computeSequentialParties(rounds);
	const totalParties = points.length;
	if (!totalParties) {
		return { totalParties: 0, average: 0 };
	}
	return {
		totalParties,
		average: computePartiesAverage(points),
	};
}

/** Moyenne cumulative recalculée à la fin de chaque manche (P1–P4). */
export function computeMancheRunningAveragePoints(points) {
	if (!points?.length) return [];

	let sum = 0;
	let count = 0;
	const linePoints = [];
	let currentManche = points[0].manche;
	let mancheStartIndex = 0;

	const flushManche = (endIndex) => {
		for (let i = mancheStartIndex; i <= endIndex; i += 1) {
			sum += points[i].value;
			count += 1;
		}
		linePoints.push({
			barIndex: endIndex,
			runningAvg: sum / count,
			manche: currentManche,
			label: String(currentManche),
		});
	};

	points.forEach((pt, index) => {
		if (pt.manche !== currentManche) {
			flushManche(index - 1);
			currentManche = pt.manche;
			mancheStartIndex = index;
		}
	});

	flushManche(points.length - 1);
	return linePoints;
}

export function mancheBarColor(manche, part) {
	const hue = MANCHE_HUES[(manche - 1) % MANCHE_HUES.length] ?? 200;
	const light = 48 + (part - 1) * 7;
	return `hsl(${hue}, 62%, ${light}%)`;
}

/** Palette : une teinte par saison (ordre chronologique des points). */
export function buildSeasonYearColorMap(points) {
	const years = [];
	const seen = new Set();
	(points ?? []).forEach((pt) => {
		if (pt.seasonYear != null && !seen.has(pt.seasonYear)) {
			seen.add(pt.seasonYear);
			years.push(pt.seasonYear);
		}
	});
	const map = new Map();
	years.forEach((year, index) => {
		const hue = SEASON_HUES[index % SEASON_HUES.length] ?? 200;
		map.set(year, `hsl(${hue}, 62%, 48%)`);
	});
	return map;
}

export function resolvePartyBarColor(point, seasonColorMap) {
	if (point.seasonYear != null && seasonColorMap?.has(point.seasonYear)) {
		return seasonColorMap.get(point.seasonYear);
	}
	return mancheBarColor(point.manche, point.part);
}

/** Année centrée sous la première manche jouée de chaque saison (P1 si absent → manche suivante). */
export function computeSeasonYearLabelPositions(bars) {
	const firstMancheByYear = new Map();
	(bars ?? []).forEach((bar) => {
		if (bar.seasonYear == null) return;
		const current = firstMancheByYear.get(bar.seasonYear);
		if (current == null || bar.manche < current) {
			firstMancheByYear.set(bar.seasonYear, bar.manche);
		}
	});

	const boundsByYear = new Map();
	(bars ?? []).forEach((bar) => {
		if (bar.seasonYear == null) return;
		if (bar.manche !== firstMancheByYear.get(bar.seasonYear)) return;
		const right = bar.x + bar.w;
		if (!boundsByYear.has(bar.seasonYear)) {
			boundsByYear.set(bar.seasonYear, { minX: bar.x, maxX: right });
		} else {
			const group = boundsByYear.get(bar.seasonYear);
			group.minX = Math.min(group.minX, bar.x);
			group.maxX = Math.max(group.maxX, right);
		}
	});

	return [...boundsByYear.entries()]
		.sort(([a], [b]) => a - b)
		.map(([year, { minX, maxX }]) => ({
			year,
			x: (minX + maxX) / 2,
			manche: firstMancheByYear.get(year),
		}));
}

export function computeScoreDistribution(rounds, buckets = PLAYER_SCORE_BUCKETS) {
	const scores = collectPartyScores(rounds);
	const total = scores.length || 1;
	return buckets.map((bucket) => {
		const count = scores.filter((s) => bucket.match(s)).length;
		return {
			...bucket,
			count,
			percent: Math.round((count / total) * 100),
		};
	}).filter((b) => b.count > 0);
}

/** Part de chaque P1–P4 dans le total quilles (poids = quilles pour le camembert). */
export function computeSeriesPartyDistribution(
	rounds,
	buckets = PLAYER_SERIES_PARTY_BUCKETS,
) {
	const hasP4 = rounds.some((round) => Number(round.quilles4) > 0);
	const activeBuckets = buckets.filter((bucket) => bucket.id !== 'p4' || hasP4);

	const rows = activeBuckets.map((bucket) => {
		let quilles = 0;
		let parties = 0;
		rounds.forEach((round) => {
			const n = Number(round[bucket.key]);
			if (n > 0) {
				quilles += n;
				parties += 1;
			}
		});
		return { ...bucket, quilles, parties };
	}).filter((row) => row.quilles > 0);

	const totalQuilles = rows.reduce((sum, row) => sum + row.quilles, 0);
	if (!totalQuilles) return { slices: [], totalParties: 0, totalQuilles: 0 };

	const totalParties = rows.reduce((sum, row) => sum + row.parties, 0);
	const slices = rows.map((row) => ({
		id: row.id,
		label: row.label,
		color: row.color,
		colorEnd: row.colorEnd,
		count: row.quilles,
		parties: row.parties,
		quilles: row.quilles,
		percent: Math.round((row.quilles / totalQuilles) * 100),
	}));

	return { slices, totalParties, totalQuilles };
}

function seriesAvgToBarPct(average, maxAverage) {
	if (!Number.isFinite(maxAverage) || maxAverage <= 0) return 0;
	return Math.min(100, Math.max(0, (average / maxAverage) * 100));
}

/**
 * Écart P1–P4 vs moyenne saison (= équilibre : part égale par partie de série).
 * @param {number} fairAverage — moyenne globale du joueur (ex. 48,93)
 */
export function computeSeriesPartyStats(
	rounds,
	buckets = PLAYER_SERIES_PARTY_BUCKETS,
	fairAverage = null,
) {
	const { slices, totalParties, totalQuilles } = computeSeriesPartyDistribution(rounds, buckets);
	const fair = Number.isFinite(fairAverage) && fairAverage > 0
		? fairAverage
		: (totalParties > 0 ? totalQuilles / totalParties : 0);

	const slotCount = slices.length || 3;
	const fairQuillesPerSlot = totalQuilles > 0 ? totalQuilles / slotCount : 0;

	const slots = slices.map((slice) => {
		const average = slice.parties > 0 ? slice.quilles / slice.parties : 0;
		const deltaAvg = average - fair;
		return {
			...slice,
			average,
			sharePct: totalQuilles > 0 ? (slice.quilles / totalQuilles) * 100 : 0,
			deltaAvg,
			deltaQuilles: slice.quilles - fairQuillesPerSlot,
			isGain: deltaAvg > 0.15,
			isLoss: deltaAvg < -0.15,
			isBalanced: Math.abs(deltaAvg) <= 0.15,
		};
	});

	const maxAverage = Math.max(
		fair,
		...slots.map((s) => s.average),
		1,
	);
	const fairBarPct = seriesAvgToBarPct(fair, maxAverage);

	return {
		slots: slots.map((slot) => ({
			...slot,
			barPct: seriesAvgToBarPct(slot.average, maxAverage),
			fairBarPct,
		})),
		totalParties,
		totalQuilles,
		fairAverage: fair,
		maxAverage,
	};
}

/** Graduations Y dynamiques : min arrondi à la dizaine inf., max à la dizaine sup. */
export function buildYAxis(values, runningAvgs) {
	const all = [...(values ?? []), ...(runningAvgs ?? [])].filter(Number.isFinite);
	if (!all.length) {
		return { minY: 0, maxY: 60, ticks: [0, 20, 40, 60] };
	}

	let minY = Math.floor(Math.min(...all) / 10) * 10;
	let maxY = Math.ceil(Math.max(...all) / 10) * 10;
	if (maxY <= minY) maxY = minY + 10;

	const ticks = [];
	for (let tick = minY; tick <= maxY; tick += 10) {
		ticks.push(tick);
	}
	return { minY, maxY, ticks };
}

/** Axe Y serré pour l'évolution de moyenne carrière (min−1 arrondi inf., max+1 arrondi sup.). */
export function buildCareerAverageYAxis(values, extras = []) {
	const all = [...(values ?? []), ...(extras ?? [])].filter(Number.isFinite);
	if (!all.length) {
		return { minY: 0, maxY: 60, ticks: [0, 10, 20, 30, 40, 50, 60] };
	}

	const dataMin = Math.min(...all);
	const dataMax = Math.max(...all);
	let minY = Math.floor(dataMin - 1);
	let maxY = Math.ceil(dataMax + 1);
	if (maxY <= minY) maxY = minY + 2;

	const range = maxY - minY;
	let step = 1;
	if (range > 12) step = 2;
	if (range > 24) step = 5;

	const ticks = [];
	for (let tick = minY; tick <= maxY; tick += step) {
		ticks.push(tick);
	}
	if (ticks[ticks.length - 1] !== maxY) {
		ticks.push(maxY);
	}

	return { minY, maxY, ticks };
}

/** Chemin SVG en escalier : horizontaux entre manches, verticaux aux changements. */
export function buildMancheAvgStepPath(lineMarkers, yScale) {
	if (lineMarkers.length < 2) return '';

	const coords = lineMarkers.map((pt) => ({
		x: pt.bar.x + pt.bar.w / 2,
		y: yScale(pt.runningAvg),
	}));

	let d = `M ${coords[0].x} ${coords[0].y}`;
	for (let i = 1; i < coords.length; i += 1) {
		d += ` H ${coords[i].x} V ${coords[i].y}`;
	}
	return d;
}

export function filterPlayerRounds(player, filters) {
	if (!player?.rounds?.length) return [];
	const { zone, championship, category } = filters || {};
	if (!zone || !championship || !category) return player.rounds;
	return player.rounds.filter(
		(r) => r.zone === zone && r.championship === championship && r.category === category,
	);
}

/** Arc donut (SVG path). */
export function describeDonutSlice(cx, cy, rOuter, rInner, startAngle, endAngle) {
	const large = endAngle - startAngle > Math.PI ? 1 : 0;
	const xo1 = cx + rOuter * Math.cos(startAngle);
	const yo1 = cy + rOuter * Math.sin(startAngle);
	const xo2 = cx + rOuter * Math.cos(endAngle);
	const yo2 = cy + rOuter * Math.sin(endAngle);
	const xi1 = cx + rInner * Math.cos(endAngle);
	const yi1 = cy + rInner * Math.sin(endAngle);
	const xi2 = cx + rInner * Math.cos(startAngle);
	const yi2 = cy + rInner * Math.sin(startAngle);
	return `M ${xo1} ${yo1} A ${rOuter} ${rOuter} 0 ${large} 1 ${xo2} ${yo2} L ${xi1} ${yi1} A ${rInner} ${rInner} 0 ${large} 0 ${xi2} ${yi2} Z`;
}

const PARTY_SCORE_KEYS = ['quilles1', 'quilles2', 'quilles3', 'quilles4'];
const MAX_PARTY_STAGE = 8;

export function buildPlayerRoundRowKey(round) {
	return [
		round?.seasonYear ?? '',
		round?.round ?? '',
		round?.teamNumber ?? '',
		round?.category ?? '',
		round?.championship ?? '',
	].join('::');
}

function formatPartyRecordDate(ctx) {
	if (!ctx?.seasonYear) return null;
	const manche = ctx.round ? String(Number(ctx.round)) : '—';
	return `${ctx.seasonYear} · M${manche}`;
}

function buildRecordTarget(events, start, partyCount) {
	const windowEvents = events.slice(start, start + partyCount);
	if (!windowEvents.length) return null;
	return {
		focusRowKey: windowEvents[0].rowKey,
		cells: windowEvents.map((event) => ({
			rowKey: event.rowKey,
			partyIndex: event.partyIndex,
		})),
	};
}

/** Parties jouées consécutivement (P1→P2→… sans trou), ordre chronologique. */
function collectSuccessivePartyEvents(rounds) {
	const sortedRounds = [...(rounds ?? [])].sort(compareRoundsChronological);

	const events = [];
	sortedRounds.forEach((round) => {
		const rowKey = buildPlayerRoundRowKey(round);
		for (let i = 0; i < PARTY_SCORE_KEYS.length; i += 1) {
			const value = Number(round[PARTY_SCORE_KEYS[i]]);
			if (!Number.isFinite(value) || value <= 0) break;
			events.push({
				value,
				seasonYear: round.seasonYear,
				round: round.round,
				partyIndex: i + 1,
				rowKey,
			});
		}
	});
	return events;
}

/** Min / max cumulés sur 1 à 8 parties successives (fenêtre glissante). */
export function computePartyStageRecords(rounds) {
	const events = collectSuccessivePartyEvents(rounds);

	return Array.from({ length: MAX_PARTY_STAGE }, (_, index) => {
		const partyCount = index + 1;
		if (events.length < partyCount) {
			return {
				partyCount,
				min: null,
				max: null,
				minDate: null,
				maxDate: null,
				minTarget: null,
				maxTarget: null,
			};
		}

		let min = null;
		let max = null;
		let minCtx = null;
		let maxCtx = null;
		let minStart = 0;
		let maxStart = 0;

		for (let start = 0; start <= events.length - partyCount; start += 1) {
			if (partyCount > 1 && events[start].partyIndex !== 1) continue;
			let sum = 0;
			for (let offset = 0; offset < partyCount; offset += 1) {
				sum += events[start + offset].value;
			}
			const endCtx = events[start + partyCount - 1];
			if (min === null || sum < min) {
				min = sum;
				minCtx = endCtx;
				minStart = start;
			}
			if (max === null || sum > max) {
				max = sum;
				maxCtx = endCtx;
				maxStart = start;
			}
		}

		return {
			partyCount,
			min,
			max,
			minDate: formatPartyRecordDate(minCtx),
			maxDate: formatPartyRecordDate(maxCtx),
			minTarget: min !== null ? buildRecordTarget(events, minStart, partyCount) : null,
			maxTarget: max !== null ? buildRecordTarget(events, maxStart, partyCount) : null,
		};
	});
}

/** Fréquence de chaque score de partie (P1–P4) sur la carrière. */
export function computePartyScoreFrequency(rounds) {
	const counts = new Map();
	let totalParties = 0;

	(rounds ?? []).forEach((round) => {
		PARTY_SCORE_KEYS.forEach((key) => {
			const score = Number(round[key]);
			if (!Number.isFinite(score) || score <= 0) return;
			counts.set(score, (counts.get(score) ?? 0) + 1);
			totalParties += 1;
		});
	});

	const frequencies = [...counts.entries()]
		.map(([score, count]) => ({
			score,
			count,
			ratio: totalParties > 0 ? count / totalParties : 0,
		}))
		.sort((a, b) => a.score - b.score);

	return { totalParties, frequencies };
}
